import { Box, Button, Grid, Stack, Tooltip, Typography } from '@mui/material'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { DefaultAccordion } from '../generic/DefaultAccordion'
import SmartTextField from '../generic/smarttextfield/SmartTextField'
import { DateTimePicker } from '@mui/x-date-pickers'
import { useDateFormatter } from '../generic/hooks/useDateFormatter'
import SmartSelect from '../generic/smartSelect/SmartSelect'
import { useCallback, useEffect, useState } from 'react'
import { apiGet, apiPost, apiPut } from '../generic/Api_Functions'
import { SaveRounded, TextSnippetOutlined } from '@mui/icons-material'
import WarningIcon from '@mui/icons-material/Warning'
import EmailIcon from '@mui/icons-material/Email'
import AlertUI from '../generic/AlertUI'
import { SmartDialog } from '../generic/utilities/SmartDialog'
import Document from '../configuration/document/Document'
import MailForm from '../generic/mail/MailForm'
import useFormUtils from '../configuration/orders/useFormUtils'
import { useTheme } from '@emotion/react'

const defaultTemplate = 'incident-report'
const gdelsQualitative = 'GDELS-qualitative-incident'
const gdelsQuantitative = 'GDELS-quantitative-incident'

/**
 *
 * @param {object} props
 * @param {import('../generic/utilities/objectStructures/materialreception/materialReception').IncidenceForm} props.incidence
 * @param {function} props.setIncidence
 * @returns
 */
const IncidenceForm = ({ incidence, setIncidence }) => {
  const { t_ } = useSmartTranslation()
  const { format } = useDateFormatter()
  const [firstLoad, setFirstLoad] = useState(true)
  /** @type {[import('../generic/utilities/objectStructures/incidences/Incidence').Incidence[], React.Dispatch<SetStateAction<import('../generic/utilities/objectStructures/incidences/Incidence').Incidence[]>>]} */
  const [incidences, setIncidences] = useState([])
  const [AlertElement, showAlert] = AlertUI()
  const [showDocument, setShowDocument] = useState(false)
  const [isDownloadingFile, setIsDownloadingFile] = useState(false)
  const [validate, setValidate] = useState(false)
  const [showSendEmail, setShowSendEmail] = useState(false)
  const [mailTemplate, setMailTemplate] = useState(
    {
      to: [],
      subject: '',
      body: '',
      templateName: '',
      templateVariables: {},
      attachments: []
    }
  )
  const [possibleDocuments, setPossibleDocuments] = useState([])
  const { isEjercito } = useFormUtils()
  const theme = useTheme()

  const getTemplateName = (currentIncidence) => {
    if (currentIncidence?.classIncidence === 'QUALITATIVE') {
      return gdelsQualitative
    }
    if (currentIncidence?.classIncidence === 'QUANTITATIVE') {
      return gdelsQuantitative
    }
    return defaultTemplate
  }

  useEffect(() => {
    const getIncidences = async () => {
      const data = await apiGet('incidencesBasicData')
      setIncidences(data)
      setFirstLoad(true)
    }
    if (firstLoad) {
      getIncidences()
    }
  }, [firstLoad])

  const checkIfIsNotEmpty = (value, fieldName) => {
    if (!value) {
      return t_(fieldName) + ' ' + t_('Can not be empty').toLocaleLowerCase()
    }
  }

  const fetchIncidence = async () => {
    if (!incidence.id) {
      return
    }
    const data = await apiGet('materialReceptionIncidence/getIncidence/' + incidence.id)
    setIncidence(data)
  }

  const checkIfIsNumber = (value, fieldName) => {
    const error = checkIfIsNotEmpty(value, fieldName)
    if (error) {
      return error
    }
    if (isNaN(value) || Number(value) <= 0) {
      return t_(fieldName) + ' ' + t_('must be a number greater or equal 0').toLocaleLowerCase()
    }
  }

  const getTypeLabel = () => {
    if (!incidence.incidence && validate) {
      return (
        <Tooltip placement='top' title={t_('Incidence Type') + ' ' + t_('Can not be empty').toLocaleLowerCase()}>
          <WarningIcon />  *
        </Tooltip>
      )
    }

    return t_('Incidence Type') + ' *'
  }

  const getShowWarning = () => {
    showAlert({ severity: 'warning', title: t_('Fix errors before saving') })
    return false
  }

  const loadTemplate = useCallback(async (currentIncidence) => {
    const templateName = getTemplateName(currentIncidence)
    const result = await apiGet('template/' + templateName)
    if (!result) {
      return null
    }
    return {
      to: result?.to || [],
      body: '',
      templateName: result.templateName,
      templateVariables: {},
      attachments: []
    }
  }, [])

  useEffect(() => {
    const syncTemplate = async () => {
      const loadedTemplate = await loadTemplate(incidence)
      if (!loadedTemplate) {
        return
      }
      setMailTemplate(prev => ({
        ...prev,
        ...loadedTemplate
      }))
    }
    syncTemplate()
  }, [isEjercito, incidence, loadTemplate])

  /**
  * @returns {import('../generic/utilities/objectStructures/globalStructures').NameValue[]}
  */
  const getEmailData = (currentIncidence = incidence) => {
    if (!currentIncidence) {
      return []
    }
    /** @returns {import('../generic/utilities/objectStructures/incidences/Incidence').IncidenceMailData} */
    const mappedIncidence = {
      incidenceCode: currentIncidence.code,
      description: currentIncidence.description,
      recordCodePosition: currentIncidence.deliveryNote.code + ' / ' + (currentIncidence.recordPos ?? ''),
      purchaseOrder: currentIncidence.purchaseOrder + ' / ' + (currentIncidence.purchaseOrderPosition ?? ''),
      deliveryNote: currentIncidence.deliveryNote.number,
      deliveryNoteDate: new Date(currentIncidence.deliveryNote.date).toLocaleString(),
      rawMaterialReferenceCode: currentIncidence.rawMaterialReferenceCode + ' - ' + currentIncidence.rawMaterialReferenceDescription,
      receivedQuantity: currentIncidence.receivedQuantity,
      registryNumber: currentIncidence.regNumber,
      supplier: currentIncidence.supplierCode + ' - ' + (currentIncidence.supplierName ?? ''),
      serialNumber: currentIncidence.serialNumber,
      project: currentIncidence.project,
      text: currentIncidence.text,
      solved: currentIncidence.solved,
      solvedText: currentIncidence.solvedText
    }

    return [
      { name: t_('Incidence No.'), value: mappedIncidence.incidenceCode || '' },
      { name: t_('Description'), value: mappedIncidence.description || '' },
      { name: t_('Order') + '/' + t_('Position'), value: mappedIncidence.purchaseOrder || '' },
      { name: t_('Entrance') + ' Cod./Pos.', value: mappedIncidence.recordCodePosition || '' },
      { name: t_('Delivery note'), value: mappedIncidence.deliveryNote || '' },
      { name: t_('Item'), value: mappedIncidence.rawMaterialReferenceCode || '' },
      { name: t_('Entry date'), value: mappedIncidence.deliveryNoteDate || '' },
      { name: 'Ctd.N. ' + t_('Delivery'), value: mappedIncidence.receivedQuantity || '' },
      { name: t_('Record No.'), value: mappedIncidence.registryNumber || '' },
      { name: t_('Supplier'), value: mappedIncidence.supplier || '' },
      { name: t_('Serial No.'), value: mappedIncidence.serialNumber || '' },
      { name: t_('Project'), value: mappedIncidence.project || '' },
      { name: t_('Texto'), value: mappedIncidence.text || '' },
      { name: t_('Solved'), value: mappedIncidence.solved || '' },

      mappedIncidence.solved === 'YES' && {
        name: t_('Solved text'),
        value: mappedIncidence.solvedText || ''
      }

    ].filter(Boolean)
  }

  useEffect(() => {
    const loadDocuments = async () => {
      if (incidence === null || incidence.id === null) {
        return
      }
      const docs = await fetchDocuments(incidence.id)
      setPossibleDocuments(docs)
    }
    loadDocuments()
  }, [incidence, showSendEmail])

  const fetchDocuments = async (incidenceId) => {
    if (!incidenceId) {
      return []
    }
    const resourceType = 'MATERIAL_RECEPTION_INCIDENCE'
    const data = await apiGet('document/getAll/' + resourceType + '/' + incidenceId)
    return data?.map(doc => ({ id: doc.id, code: doc.fileName, name: doc.documentName })) || []
  }

  const preSaveCheck = () => {
    setValidate(true)
    if (checkIfIsNumber(incidence.purchaseOrder, 'Order')) {
      return getShowWarning()
    }
    if (checkIfIsNumber(incidence.purchaseOrderPosition, 'Position')) {
      return getShowWarning()
    }

    if (checkIfIsNotEmpty(incidence.rawMaterialReferenceCode, 'Part')) {
      return getShowWarning()
    }

    if (checkIfIsNotEmpty(incidence.rawMaterialReferenceDescription, 'Description')) {
      return getShowWarning()
    }

    if (checkIfIsNotEmpty(incidence.project, 'Project')) {
      return getShowWarning()
    }

    if (checkIfIsNotEmpty(incidence.supplierCode, 'Supplier')) {
      return getShowWarning()
    }

    if (checkIfIsNotEmpty(incidence.supplierName, 'Supplier name')) {
      return getShowWarning()
    }

    if (checkIfIsNotEmpty(incidence.description, 'Description')) {
      return getShowWarning()
    }

    if (checkIfIsNumber(incidence.entryQuantity, 'Entry quantity')) {
      return getShowWarning()
    }

    if (checkIfIsNumber(incidence.receivedQuantity, 'Received quantity')) {
      return getShowWarning()
    }

    if (!incidence.incidence) {
      return getShowWarning()
    }

    if (checkIfIsNotEmpty(incidence.location, 'Location')) {
      return getShowWarning()
    }

    if (checkIfIsNotEmpty(incidence.solvedText, 'Solved text') && incidence.solved === 'YES') {
      return getShowWarning()
    }

    setValidate(false)
    return true
  }

  const saveIncidence = async () => {
    if (!preSaveCheck()) {
      return null
    }

    let result

    if (!incidence.code) {
      result = await apiPost('materialReceptionIncidence/create', incidence).catch(error => {
        showAlert({ severity: 'error', title: t_(error.message) })
      })
    } else {
      result = await apiPut('materialReceptionIncidence/update', incidence).catch(error => {
        showAlert({ severity: 'error', title: t_(error.message) })
      })
    }
    if (!result) {
      return null
    }
    showAlert({ severity: 'success', title: t_('Incidence saved') })
    setIncidence(result)
    return result
  }

  const saveAndSendEmail = async () => {
    const savedIncidence = await saveIncidence()
    if (!savedIncidence) {
      return
    }

    const data = { ...mailTemplate }
    data.subject = 'MIC INCIDENCE: ' + savedIncidence.code + ' - ' + savedIncidence.description + ' - ' + savedIncidence.supplierCode + ' - ' + savedIncidence.supplierName
    setMailTemplate(data)
    setShowSendEmail(true)
  }

  const updateEmailSentDate = async (incidenceId = incidence?.id) => {
    if (!incidenceId) {
      return
    }
    await apiPut('materialReceptionIncidence/updateEmailSent/' + incidenceId)
  }

  const buildBodyFromData = (data) => {
    const obj = {}
    data?.forEach(item => {
      obj[item.name] = item.value
    })
    return obj
  }

  const sendSolvedEmailInBackground = async (savedIncidence) => {
    try {
      const template = await loadTemplate(savedIncidence)
      if (!template || !template.to?.length) {
        return
      }
      const attachments = await fetchDocuments(savedIncidence.id)
      const emailData = getEmailData(savedIncidence)
      const payload = {
        ...template,
        subject: 'MIC INCIDENCE: ' + savedIncidence.code + ' - ' + savedIncidence.description + ' - ' + savedIncidence.supplierCode + ' - ' + savedIncidence.supplierName,
        attachments,
        body: null,
        templateVariables: buildBodyFromData(emailData)
      }
      await apiPost('sendMailTemplate', payload)
      await updateEmailSentDate(savedIncidence.id)
      fetchIncidence()
    } catch (error) {
      const backendMessage = error?.response?.data?.message || error?.response?.data || error.message
      showAlert({
        severity: 'error',
        title: t_('Failed to send email:'),
        message: t_(backendMessage)
      })
    }
  }

  const saveIncidenceWithSolvedEmail = async () => {
    const savedIncidence = await saveIncidence()
    if (!savedIncidence) {
      return
    }
    const incidenceSolved = savedIncidence.solved === 'YES' || savedIncidence.solved === true
    if (incidenceSolved) {
      sendSolvedEmailInBackground(savedIncidence)
    }
  }

  return (
    <div style={{ marginTop: '1rem', marginRight: '1rem' }}>
      {AlertElement}
      <SmartDialog
        title={{ icon: <EmailIcon />, render: <Typography variant='h5'>{t_('Send Email')}</Typography> }}
        setOpen={showSendEmail}
        closeCallback={() => {
          setShowSendEmail(false)
          fetchIncidence()
        }}
        accept={false}
        cancel={false}
        close
        paperWidth='40rem'
        renderComponent={
          <MailForm
            template={{ ...mailTemplate, attachments: possibleDocuments }}
            data={getEmailData()}
            attachmentOptions={possibleDocuments}
            canEditContent={false}
            onSave={() => { updateEmailSentDate() }}
          />
        }
      />
      <SmartDialog
        closeCallback={() => setShowDocument(false)}
        accept={false}
        cancel={false}
        close
        disableButtons={isDownloadingFile}
        renderComponent={<Document reference={{ id: incidence.id, code: incidence.code, name: incidence.code }} resourceType='MATERIAL_RECEPTION_INCIDENCE' isDownloadingFile={isDownloadingFile} setIsDownloadingFile={setIsDownloadingFile} />}
        setOpen={showDocument}
      />
      <Stack direction='row' justifyContent='right'>
        {(incidence.closeDate === undefined || incidence.closeDate === null) && (
          <Button
            sx={{ borderColor: 'transparent' }}
            color='primary'
            variant='outlined'
            onClick={() => saveIncidenceWithSolvedEmail()}
          >
            <SaveRounded sx={{ fontSize: '1.2em' }} />
            <Typography sx={{ marginLeft: '0.4em', fontSize: '0.875rem' }}>{t_('Save').toUpperCase()}</Typography>
          </Button>
        )}
        {(incidence.closeDate === undefined || incidence.closeDate === null) && (
          <Button
            disabled={!incidence.code}
            sx={{ borderColor: 'transparent' }}
            color='primary'
            variant='outlined'
            onClick={() => { saveAndSendEmail() }}
          >
            <EmailIcon sx={{ fontSize: '1.2em' }} />
            <Typography sx={{ marginLeft: '0.4em', fontSize: '0.875rem' }}>{t_('Send Email').toUpperCase()}</Typography>
          </Button>
        )}
        <Button
          sx={{ borderColor: 'transparent' }}
          color='primary'
          variant='outlined'
          onClick={() => setShowDocument(true)}
          disabled={!incidence.code}
        >
          <TextSnippetOutlined sx={{ fontSize: '1.2em' }} />
          <Typography sx={{ marginLeft: '0.4em', fontSize: '0.875rem' }}>{t_('Documents')}</Typography>
        </Button>
      </Stack>
      <DefaultAccordion headerRender={t_('Incidence').toUpperCase()}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={2}>
            <SmartTextField
              label={t_('Code')}
              disabled
              value={incidence.code}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <SmartTextField
              label={t_('Order')}
              value={incidence.purchaseOrder}
              onChange={(newValue) => {
                const data = { ...incidence }
                data.purchaseOrder = newValue
                setIncidence(data)
              }}
              checkError={(v) => checkIfIsNumber(v, 'Order')}
              forceCheckError={validate}
              required
              disabled={incidence.closeDate !== undefined && incidence.closeDate !== null}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <SmartTextField
              label={t_('Position')}
              value={incidence.purchaseOrderPosition}
              onChange={(newValue) => {
                const data = { ...incidence }
                data.purchaseOrderPosition = newValue
                setIncidence(data)
              }}
              checkError={(v) => checkIfIsNumber(v, 'Position')}
              forceCheckError={validate}
              required
              disabled={incidence.closeDate !== undefined && incidence.closeDate !== null}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <SmartTextField
              label={t_('Delivery note')}
              disabled
              value={incidence.deliveryNote.number}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <SmartTextField
              label={t_('Raw material')}
              value={incidence.rawMaterialReferenceCode}
              onChange={(newValue) => {
                const data = { ...incidence }
                data.rawMaterialReferenceCode = newValue
                setIncidence(data)
              }}
              checkError={(v) => checkIfIsNotEmpty(v, 'Raw material')}
              forceCheckError={validate}
              required
              disabled={incidence.closeDate !== undefined && incidence.closeDate !== null}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <SmartTextField
              label={t_('Raw material description')}
              value={incidence.rawMaterialReferenceDescription}
              onChange={(newValue) => {
                const data = { ...incidence }
                data.rawMaterialReferenceDescription = newValue
                setIncidence(data)
              }}
              checkError={(v) => checkIfIsNotEmpty(v, 'Raw material description')}
              forceCheckError={validate}
              required
              disabled={incidence.closeDate !== undefined && incidence.closeDate !== null}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <SmartTextField
              label={t_('Project')}
              value={incidence.project}
              onChange={(newValue) => {
                const data = { ...incidence }
                data.project = newValue
                setIncidence(data)
              }}
              checkError={(v) => checkIfIsNotEmpty(v, 'Project')}
              forceCheckError={validate}
              required
              disabled={incidence.closeDate !== undefined && incidence.closeDate !== null}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <SmartTextField
              label={t_('Supplier')}
              value={incidence.supplierCode}
              onChange={(newValue) => {
                const data = { ...incidence }
                data.supplierCode = newValue
                setIncidence(data)
              }}
              checkError={(v) => checkIfIsNotEmpty(v, 'Supplier')}
              forceCheckError={validate}
              required
              disabled={incidence.closeDate !== undefined && incidence.closeDate !== null}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <SmartTextField
              label={t_('Supplier name')}
              value={incidence.supplierName}
              onChange={(newValue) => {
                const data = { ...incidence }
                data.supplierName = newValue
                setIncidence(data)
              }}
              checkError={(v) => checkIfIsNotEmpty(v, 'Supplier name')}
              forceCheckError={validate}
              required
              disabled={incidence.closeDate !== undefined && incidence.closeDate !== null}
            />
          </Grid>
          <Grid item xs={12} sm={6} textAlign='left' style={{ paddingTop: 8 }} />
          <Grid item xs={12} sm={6} textAlign='left' style={{ paddingTop: 8 }}>
            <Box
              component='fieldset'
              sx={{
                borderColor: incidence.solved === 'YES' && !incidence.solvedText ? theme.palette.error.main : '#ffffff4d',
                borderRadius: 1,
                padding: '4px 14px 14px',
                '&:focus-within': {
                  borderColor: incidence.solved === 'YES' && !incidence.solvedText ? theme.palette.error.main : theme.palette.primary.main
                }
              }}
            >
              <Box
                component='legend'
                sx={{
                  px: 1,
                  fontSize: '0.75rem',
                  opacity: (incidence.closeDate !== undefined && incidence.closeDate !== null) ? 0.5 : 1,
                  color: incidence.solved === 'YES' && !incidence.solvedText ? theme.palette.error.main : undefined
                }}
              >
                {incidence.solved === 'YES' && !incidence.solvedText
                  ? (
                    <Tooltip placement='top' title={t_('Solved text') + ' ' + t_('Can not be empty').toLocaleLowerCase()} style={{ fontSize: '1.2rem' }}>
                      <WarningIcon />
                    </Tooltip>
                    )
                  : t_('Solved text')}
              </Box>
              <Box
                component='textarea'
                style={{
                  width: '100%',
                  border: 'none',
                  outline: 'none',
                  resize: 'vertical',
                  font: 'inherit',
                  background: 'transparent',
                  padding: 0,
                  minHeight: '5rem'
                }}
                onChange={(event) => {
                  const data = { ...incidence }
                  data.solvedText = event.target.value
                  setIncidence(data)
                }}
                value={incidence.solvedText}
                disabled={(incidence.closeDate !== undefined && incidence.closeDate !== null) || incidence.solved === 'NO'}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} textAlign='left' style={{ paddingTop: 8 }}>
            <Box
              component='fieldset'
              sx={{
                borderColor: '#ffffff4d',
                borderRadius: 1,
                padding: '4px 14px 14px',
                '&:focus-within': {
                  borderColor: theme.palette.primary.main
                }
              }}
            >
              <Box
                component='legend'
                sx={{
                  px: 1,
                  fontSize: '0.75rem',
                  opacity: (incidence.closeDate !== undefined && incidence.closeDate !== null) ? 0.5 : 1
                }}
              >
                {t_('Text')}
              </Box>
              <Box
                component='textarea'
                style={{
                  width: '100%',
                  border: 'none',
                  outline: 'none',
                  resize: 'vertical',
                  font: 'inherit',
                  background: 'transparent',
                  padding: 0,
                  minHeight: '5rem'
                }}
                onChange={(event) => {
                  const data = { ...incidence }
                  data.text = event.target.value
                  setIncidence(data)
                }}
                value={incidence.text}
                disabled={incidence.closeDate !== undefined && incidence.closeDate !== null}
              />
            </Box>
          </Grid>
        </Grid>
      </DefaultAccordion>
      <DefaultAccordion headerRender={t_('PROPERTIES')}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={2}>
            <DateTimePicker
              label={t_('Entry date')}
              slotProps={{ textField: { InputLabelProps: { shrink: true } } }}
              format={format.dateTime}
              ampm={false}
              disabled
              value={incidence.deliveryNote.date !== undefined && incidence.deliveryNote.date !== null ? new Date(incidence.deliveryNote.date) : undefined}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <SmartTextField
              style={{ width: '100%' }}
              label={t_('Incidence description')}
              value={incidence.description}
              onChange={(newValue) => {
                const data = { ...incidence }
                data.description = newValue
                setIncidence(data)
              }}
              checkError={(v) => checkIfIsNotEmpty(v, 'Incidence description')}
              forceCheckError={validate}
              required
              disabled={incidence.closeDate !== undefined && incidence.closeDate !== null}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <SmartTextField
              label={t_('Entry quantity')}
              value={incidence.entryQuantity}
              onChange={(newValue) => {
                const data = { ...incidence }
                data.entryQuantity = newValue
                setIncidence(data)
              }}
              checkError={(value) => checkIfIsNumber(value, 'Entry quantity')}
              forceCheckError={validate}
              required
              disabled={incidence.closeDate !== undefined && incidence.closeDate !== null}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <SmartTextField
              label={t_('Received quantity')}
              value={incidence.receivedQuantity}
              onChange={(newValue) => {
                const data = { ...incidence }
                data.receivedQuantity = newValue
                setIncidence(data)
              }}
              checkError={(value) => checkIfIsNumber(value, 'Received quantity')}
              forceCheckError={validate}
              required
              disabled={incidence.closeDate !== undefined && incidence.closeDate !== null}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <DateTimePicker
              label={t_('Review date')}
              slotProps={{ textField: { InputLabelProps: { shrink: incidence.reviewDate !== undefined } } }}
              format={format.dateTime}
              ampm={false}
              disabled={incidence.reviewDate === undefined || (incidence.closeDate !== undefined && incidence.closeDate !== null)}
              value={incidence.reviewDate !== undefined && incidence.reviewDate !== null ? new Date(incidence.reviewDate) : undefined}
              onChange={(newValue) => {
                const data = { ...incidence }
                data.reviewDate = newValue
                setIncidence(data)
              }}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <DateTimePicker
              label={t_('Update date')}
              slotProps={{ textField: { InputLabelProps: { shrink: incidence.updateDate !== undefined } } }}
              format={format.dateTime}
              ampm={false}
              disabled
              value={incidence.updateDate !== undefined && incidence.updateDate !== null ? new Date(incidence.updateDate) : undefined}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <SmartTextField
              label={t_('User')}
              disabled
              value={incidence.user.name}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <SmartTextField
              label={t_('Reg. number')}
              value={incidence.regNumber}
              onChange={(newValue) => {
                const data = { ...incidence }
                data.regNumber = newValue
                setIncidence(data)
              }}
              forceCheckError={validate}
              disabled={incidence.closeDate !== undefined && incidence.closeDate !== null}
              inputProps={{ maxLength: 10 }}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <SmartSelect
              label={t_('Solved')}
              value={t_(incidence.solved)}
              selectableOptions={[t_('YES'), t_('NO')]}
              onChange={(newValue) => {
                const data = { ...incidence }
                data.solved = 'YES'
                if (newValue === t_('NO')) {
                  data.solved = 'NO'
                }
                setIncidence(data)
              }}
              disabled={incidence.closeDate !== undefined && incidence.closeDate !== null}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <DateTimePicker
              label={t_('Close date')}
              slotProps={{ textField: { InputLabelProps: { shrink: incidence.closeDate !== undefined && incidence.closeDate !== null } } }}
              format={format.dateTime}
              ampm={false}
              disabled
              value={incidence.closeDate !== undefined && incidence.closeDate !== null ? new Date(incidence.closeDate) : undefined}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <SmartTextField
              label={t_('Serial Number')}
              value={incidence.serialNumber}
              onChange={(newValue) => {
                const data = { ...incidence }
                data.serialNumber = newValue
                setIncidence(data)
              }}
              disabled={incidence.closeDate !== undefined && incidence.closeDate !== null}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <SmartSelect
              label={getTypeLabel()}
              value={incidence.incidence?.code}
              selectableOptions={incidences.map((type) => type.code)}
              onChange={(newValue) => {
                const data = { ...incidence }
                const inc = incidences.find(incidence => incidence.code === newValue)
                data.incidence = inc
                data.classIncidence = inc.incidenceType.name
                setIncidence(data)
              }}
              error={!incidence.incidence && validate}
              disabled={incidence.closeDate !== undefined && incidence.closeDate !== null}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <SmartTextField
              label={t_('Description')}
              disabled
              value={incidence.incidence?.description}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <SmartTextField
              label={t_('Class')}
              disabled
              value={t_(incidence.classIncidence)}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <SmartTextField
              label={t_('Location')}
              value={incidence.location}
              onChange={(newValue) => {
                const data = { ...incidence }
                data.location = newValue
                setIncidence(data)
              }}
              checkError={(v) => checkIfIsNotEmpty(v, 'Location')}
              forceCheckError={validate}
              required
              disabled={incidence.closeDate !== undefined && incidence.closeDate !== null}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <DateTimePicker
              label={t_('Email date')}
              slotProps={{ textField: { InputLabelProps: { shrink: incidence.emailDate !== undefined && incidence.emailDate !== null } } }}
              format={format.dateTime}
              ampm={false}
              disabled
              value={incidence.emailDate !== undefined && incidence.emailDate !== null ? new Date(incidence.emailDate) : null}
            />
          </Grid>
        </Grid>
      </DefaultAccordion>
    </div>
  )
}

export default IncidenceForm
